import { OptimizelyProvider, createInstance, OptimizelyContext, useExperiment as useExperiment$1 } from '@optimizely/react-sdk';
export { OptimizelyContext as OptimizelySubitoContext, OptimizelyExperiment as OptimizelySubitoExperiment, OptimizelyFeature as OptimizelySubitoFeature, OptimizelyVariation as OptimizelySubitoVariation, setLogLevel as setOptimizelyLogLevel, setLogger as setOptimizelyLogger, useFeature } from '@optimizely/react-sdk';
import React, { useContext } from 'react';
import { zeroUUID, getOptimizelySdkOptions } from '@sbt-web/optimizely-sdk';
export { userIdPrefix, zeroUUID } from '@sbt-web/optimizely-sdk';
import { LogLevel, enums } from '@optimizely/optimizely-sdk';

// Exported only for test purpose
const getUserId = (userId, environmentId) => {
    if (userId) {
        return { id: userId };
    }
    if (typeof environmentId === 'string') {
        return { id: environmentId };
    }
    return (environmentId?.then((envID) => ({ id: envID })) ?? { id: zeroUUID });
};
const OptimizelySubitoProvider = function ({ optimizely, userId, environmentId, isServerSide = false, children, }) {
    const user = getUserId(userId, environmentId);
    return (React.createElement(OptimizelyProvider, { optimizely: optimizely, isServerSide: isServerSide, user: user }, children));
};

const ToStringLevels = new Map([
    [LogLevel.NOTSET, 'LOG'],
    [LogLevel.DEBUG, 'DEBUG'],
    [LogLevel.INFO, 'INFO'],
    [LogLevel.WARNING, 'WARNING'],
    [LogLevel.ERROR, 'ERROR'],
]);
// For the documentation about the logger provided with the React-SDK see:
// https://github.com/optimizely/javascript-sdk/blob/23870617c7a480f200feae37c5f0202bb18c1966/packages/logging/README.md
// With the aim of reducing the noise in console, this LogHandler redirects the output to the
// global array `window.optimizelySubitoLogs`.
// Only for the levels 'warning' and 'error' we keep the output in console.
const logHandler = {
    log: (level, message) => {
        const stringLevel = ToStringLevels.get(level) ?? 'LOG';
        const isServer = typeof window === 'undefined';
        if (!isServer) {
            window.optimizelySubitoLogs = window.optimizelySubitoLogs || [];
            window.optimizelySubitoLogs.push({ level: stringLevel, message });
        }
        if (isServer || level >= enums.LOG_LEVEL.WARNING) {
            console.log(`[OPTIMIZELY] ${stringLevel} - ${message}`);
        }
    },
};

/**
 * This value, used as collectorPath, make the dispatcher work also on server side.
 * So we can just use the frontend collector instead of handling the credentials for the backend one.
 */
const HOUSTON_FRONTEND_COLLECTOR = 'api/v1/track';
const HOUSTON_COLLECTOR_DOMAIN = 'https://collector.mpianalytics.com';
const HOUSTON_URL_TEMPLATE = Object.freeze({
    dev: 'https://assets.piutardi.it/v2/houston/experiments/%s',
    pre: 'https://assets.piutardi.it/v2/houston/experiments/%s',
    pro: 'https://assets.subito.it/v2/houston/experiments/%s',
});
/**
 * Creates an instance of the Optimizely React SDK configured for Houston integration
 * @param env The env "dev", "stage" or "prod"
 * @param userIdPrefix The optional prefix to add to all identifiers. This can save repetition but use it carefully.
 */
const createOptimizelyInstance = (env, userIdPrefix) => createInstance(getOptimizelySdkOptions({
    env,
    userIdPrefix,
    collectorPath: HOUSTON_FRONTEND_COLLECTOR,
    collectorDomain: HOUSTON_COLLECTOR_DOMAIN,
    urlTemplates: HOUSTON_URL_TEMPLATE,
}));

const useExperiment = ({ experimentAlias, overrideUserId, autoUpdate = false, }) => {
    const { optimizely } = useContext(OptimizelyContext);
    const [variation, clientReady] = useExperiment$1(experimentAlias, {
        autoUpdate,
    }, overrideUserId !== undefined ? { overrideUserId } : undefined);
    return {
        variation,
        instance: optimizely,
        clientReady,
    };
};
const useAnonymousExperiment = ({ experimentAlias, overrideEnvironmentId, autoUpdate, }) => useExperiment({
    experimentAlias,
    overrideUserId: overrideEnvironmentId,
    autoUpdate,
});
const useIdentifiedExperiment = ({ experimentAlias, overrideUserId, autoUpdate, }) => useExperiment({
    experimentAlias,
    overrideUserId,
    autoUpdate,
});

export { logHandler as OptimizelySubitoLogHandler, OptimizelySubitoProvider, createOptimizelyInstance, getUserId, useAnonymousExperiment, useIdentifiedExperiment };
//# sourceMappingURL=index.js.map
